import { ThemeTonalRange } from '@philips-internal/oswald-ui-components';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import moment, { Moment } from 'moment';

import { ApplicationRoute, ModulesRoute } from '../../../constants';
import { Endpoint } from '../../../constants/endpoints';
import ApprovalContext from '../../../services/ApprovalService';
import showSuccessToast from '../../../utils/show-toast';
import { SimpleDialog } from '../../dialog-helper/dialog-helper';

import { ApprovalProcessDialogs } from './approval-process-dialogs';
import { ApprovalOrderItemResponse, normalizeApprovalProcessBody, normalizeApprovalProcessFourthStepBody, normalizeApprovalProcessSecondStepBody, normalizeApprovalProcessThirdStepBody } from './approval-process-modal-business';
import ApprovalProcess from './approval-process-params';
import { normalizeApprovePurchaseBody } from './approve-purchase-business';
import { BalanceApprovalResponse, normalizeForwardBalanceApprovalBody } from './forward-balance-approval-business';
import { normalizeGeneratePurchaseRequestIntegrationBody, PurchaseRequestResponse } from './generate-purchase-request-integration-business';
import { normalizeLogApprovalProcessBody } from './log-approval-process-business';
import { NextStepMethod } from './next-step-method-enums';
import { findEnum } from './order-type-enum';
import { normalizeValidatePurchaseUnitValueBody, ValidatePurchaseUnitValueResponse } from './validate-purchase-unit-value-business';

export default class ApprovalProcessModal {

  static openDialog(approvalSeq: number | string, approvalProcessSeq: number | string): void {
    ApprovalProcess.set({
      approvalSeq: approvalSeq,
      approvalProcessSeq: approvalProcessSeq,
      descriptionDialog: 'pending.approval.action.approval.process',
    });
    ApprovalProcessDialogs.openDialogNextStep(ApprovalProcess.get(), NextStepMethod.CONFIRM);
  }

  static confirm(approvalSeq: number | string, approvalProcessSeq: number | string): void {
    const body = normalizeApprovalProcessBody('STEP1', approvalSeq, approvalProcessSeq);
    HttpService.post<ApprovalOrderItemResponse>(Endpoint.APPROVE, {}, { body })
      .then(approveOrderItemResponse => {
        if (approveOrderItemResponse.body?.noticeDescription != null) {
          return ApprovalProcessDialogs.openDialogOk('pending.approval.exceeded.approval.limit');
        }
        if (approveOrderItemResponse.body?.questionDescription != null) {
          ApprovalProcess.set({
            approvalSeq: approvalSeq,
            approvalProcessSeq: approvalProcessSeq,
            approveOrderItemResponse: approveOrderItemResponse.body,
            descriptionDialog: 'pending.approval.forward.authorized.approvers',
          });
          return ApprovalProcessDialogs.openDialogNextStep(ApprovalProcess.get(), NextStepMethod.VALIDATE_PURCHASE_UNIT_VALUE);
        } else if (approveOrderItemResponse.body != null) {
          this.validatePurchaseUnitValue(approveOrderItemResponse.body, approvalSeq, approvalProcessSeq);
        }
      })
      .catch((err: string) => SimpleDialog.showErrorMessage(err, ThemeTonalRange.VERY_DARK));
  }

  static validatePurchaseUnitValue(approveOrderItemResponse: ApprovalOrderItemResponse, approvalSeq: number | string, approvalProcessSeq: number | string): void {
    const { orderType, purchaseRequestNumber, documentNumber } = ApprovalContext.get();
    const body = normalizeValidatePurchaseUnitValueBody(approvalSeq, approveOrderItemResponse, findEnum(orderType));
    const purchaseNumber = purchaseRequestNumber as number;
    HttpService.post<ValidatePurchaseUnitValueResponse>(Endpoint.VALIDATE_PURCHASE_UNIT_VALUE, {}, { body })
      .then(validatePurchaseUnitValueResponse => {
        ApprovalProcess.set({
          approvalSeq,
          approvalProcessSeq,
          orderType,
          documentNumber,
          purchaseRequestNumber: purchaseNumber,
          descriptionDialog: 'pending.approval.unit.value.item.bigger.than.earlier.one',
        });
        if (validatePurchaseUnitValueResponse.body?.validate) {
          ApprovalProcessDialogs.openDialogNextStep(ApprovalProcess.get(), NextStepMethod.APPROVE_ORDER_ITEM_SECOND_STEP);
          return;
        }
        if (validatePurchaseUnitValueResponse.body?.stepTwo) {
          this.approveOrderItemSecondStep(approvalSeq, approvalProcessSeq, orderType, documentNumber, purchaseRequestNumber);
          return;
        }
        if (validatePurchaseUnitValueResponse.body?.includeBalance) {
          ApprovalProcess.set({ descriptionDialog: 'pending.approval.insufficient.balace.approve.process' });
          ApprovalProcessDialogs.openDialogNextStep(ApprovalProcess.get(), NextStepMethod.FORWARD_BALANCE_APPROVAL);
          return;
        }
        if (validatePurchaseUnitValueResponse.body?.finish) {
          this.finishOrderApproval(approvalSeq, approvalProcessSeq);
          return;
        }
      })
      .catch((err: string) => SimpleDialog.showErrorMessage(err, ThemeTonalRange.VERY_DARK));
  }

  static finishOrderApproval(approvalSeq: number | string, approvalProcessSeq: number | string): void {
    const startLogDate: Moment = moment();
    const body = normalizeLogApprovalProcessBody(approvalSeq, approvalProcessSeq, startLogDate, 'A', '');
    HttpService.post(Endpoint.GENERATE_LOG_APPROVAL_PROCESS, {}, { body })
      .then(() => this.successApprove()).then(this.redirect)
      .catch((err: string) => SimpleDialog.showErrorMessage(err, ThemeTonalRange.VERY_DARK));
  }

  static approveOrderItemSecondStep(approvalSeq: string | number, approvalProcessSeq: string | number, orderType: any, documentNumber: number, purchaseRequestNumber: any): void {
    const body = normalizeApprovalProcessSecondStepBody('STEP2', approvalSeq, approvalProcessSeq, findEnum(String(orderType)), Number(documentNumber), Number(purchaseRequestNumber));
    HttpService.post<ApprovalOrderItemResponse>(Endpoint.APPROVE, {}, { body })
      .then(approveOrderItemSecondStepResponse => {

        if (approveOrderItemSecondStepResponse.body?.abortMessage != null) {
          ApprovalProcessDialogs.openDialogOk('pending.approval.abort.pending.authorizations');
          return;
        } else if (approveOrderItemSecondStepResponse.body?.numberRecords != undefined && approveOrderItemSecondStepResponse.body?.numberRecords > 0) {
          ApprovalProcess.set({
            approvalSeq,
            approvalProcessSeq,
            orderType,
            documentNumber,
            purchaseRequestNumber,
            descriptionDialog: 'pending.approval.material.expiration.date',
          });
          ApprovalProcessDialogs.openDialogNextStep(ApprovalProcess.get(), NextStepMethod.APPROVE_PURCHASE);
          return;
        }
        this.approvePurchase(approvalSeq, approvalProcessSeq, orderType, documentNumber, purchaseRequestNumber);
      }).catch((err: string) => SimpleDialog.showErrorMessage(err, ThemeTonalRange.VERY_DARK));
  }

  static forwardBalanceApproval(approvalSeq: string | number, approvalProcessSeq: string | number): void {
    const body = normalizeForwardBalanceApprovalBody(approvalSeq, approvalProcessSeq);
    HttpService.post<BalanceApprovalResponse>(Endpoint.FORWARD_BALANCE_APPROVAL, {}, { body })
      .then(balanceApprovalResponse => {
        if (balanceApprovalResponse.body?.newSequenceProcess === 0) {
          ApprovalProcess.set({
            approvalSeq,
            approvalProcessSeq,
            descriptionDialog: 'pending.approval.no.next.level.approval.forward',
          });
          ApprovalProcessDialogs.openDialogNextStep(ApprovalProcess.get(), NextStepMethod.FINISH_ORDER_APPROVAL);
        }
      }).catch((err: string) => SimpleDialog.showErrorMessage(err, ThemeTonalRange.VERY_DARK));
  }

  static approvePurchase(approvalSeq: string | number, approvalProcessSeq: string | number, orderType: any, documentNumber: number, purchaseRequestNumber: any): void {
    const body = normalizeApprovePurchaseBody(approvalSeq, approvalProcessSeq);
    HttpService.post(Endpoint.APPROVE_PURCHASE, {}, { body })
      .then(() => this.approveOrderItemThirdStep(approvalSeq, approvalProcessSeq, orderType, documentNumber, purchaseRequestNumber))
      .catch((err: string) => SimpleDialog.showErrorMessage(err, ThemeTonalRange.VERY_DARK));
  }

  static approveOrderItemThirdStep(approvalSeq: string | number, approvalProcessSeq: string | number, orderType: any, documentNumber: number, purchaseRequestNumber: any): void {
    const body = normalizeApprovalProcessThirdStepBody('STEP3', approvalSeq, approvalProcessSeq, documentNumber, Number(purchaseRequestNumber));
    HttpService.post<ApprovalOrderItemResponse>(Endpoint.APPROVE, {}, { body })
      .then(approvalOrderItemThirdStepResponse => {
        if (approvalOrderItemThirdStepResponse.body?.integratePurchaseRequest && approvalOrderItemThirdStepResponse.body?.sendOnlyFewItemsToIntegration) {
          ApprovalProcess.set({
            purchaseRequestNumber,
            typeInterfaceIntegration: approvalOrderItemThirdStepResponse.body?.typeInterfaceIntegration,
            descriptionDialog: 'pending.approval.send.only.few.item',
          });
          ApprovalProcessDialogs.openDialogNextStep(ApprovalProcess.get(), NextStepMethod.GENERATE_PURCHASE_REQUEST_INTEGRATION);
        } else {
          this.approveOrderItemFourthStep(approvalSeq, approvalProcessSeq, orderType, documentNumber, purchaseRequestNumber);
        }
      })
      .catch((err: string) => SimpleDialog.showErrorMessage(err, ThemeTonalRange.VERY_DARK));
  }

  static generatePurchaseRequestIntegration(purchaseRequestNumber: number, typeInterfaceIntegration: string): void {
    const body = normalizeGeneratePurchaseRequestIntegrationBody(purchaseRequestNumber, typeInterfaceIntegration, 'A');
    HttpService.post<PurchaseRequestResponse>(Endpoint.GENERATE_PURCHASE_REQUEST_INTEGRATION, {}, { body })
      .then(purchaseRequestResponse => {
        if (purchaseRequestResponse.body?.requestSentWithSuccess) {
          ApprovalProcessDialogs.openDialogOk('pending.approval.request.sent.with.success');
          return;
        }
      })
      .catch((err: string) => SimpleDialog.showErrorMessage(err, ThemeTonalRange.VERY_DARK));
  }

  static approveOrderItemFourthStep(approvalSeq: string | number, approvalProcessSeq: string | number, orderType: any, documentNumber: number, purchaseRequestNumber: any): void {
    const body = normalizeApprovalProcessFourthStepBody('STEP4', approvalSeq, approvalProcessSeq, documentNumber, Number(purchaseRequestNumber), findEnum(String(orderType)));
    HttpService.post<ApprovalOrderItemResponse>(Endpoint.APPROVE, {}, { body })
      .then(() => {
        this.finishOrderApproval(approvalSeq, approvalProcessSeq);
      }).catch((err: string) => SimpleDialog.showErrorMessage(err, ThemeTonalRange.VERY_DARK));
  }

  static redirect(): void {
    applicationContext.redirect(
      `${ModulesRoute.APPROVALS}${ApplicationRoute.APPROVAL_LIST}`
    );
  }

  static successApprove(): void {
    showSuccessToast('approval-complete-toast', 'pending.approval.action.completed.successfully');
  }
}
